import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { Post, CreatePostData, UpdatePostData, PostFilters, PostFeedResponse } from "src/pages/Social/types/post.types";

export const SOCIAL_POSTS_KEY = "social-posts";
export const SOCIAL_POST_KEY = "social-post";
export const SOCIAL_FEED_KEY = "social-feed";

// Get posts with infinite scroll
export const useGetPosts = (filters?: PostFilters) => {
    return useInfiniteQuery({
        queryKey: [SOCIAL_POSTS_KEY, filters],
        queryFn: async ({ pageParam = 1 }) => {
            const response = await loggedInApiCall.get("/api/social/posts", {
                params: {
                    page: pageParam,
                    "order[createdAt]": "desc",
                    ...filters,
                },
            });
            return response.data;
        },
        getNextPageParam: (lastPage) => {
            return lastPage.pagination?.hasMore ? lastPage.pagination.page + 1 : undefined;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

// Get post feed
export const useGetPostFeed = (feedType: "timeline" | "trending" | "following" = "timeline", topicFilter?: number) => {
    return useInfiniteQuery({
        queryKey: [SOCIAL_FEED_KEY, feedType, topicFilter],
        queryFn: async ({ pageParam = 1 }) => {
            const params: any = {
                page: pageParam,
                itemsPerPage: 20,
                "order[createdAt]": "desc",
            };

            // Add topic filter if provided
            if (topicFilter) {
                params["topics"] = topicFilter;
            } else {
                // Only add feedType if no topic filter (topic filter overrides feedType)
                params.feedType = feedType;
            }

            const response = await loggedInApiCall.get("/api/social/posts", {
                params,
            });

            // Transform API Platform Hydra collection to expected format
            const hydraData = response.data;
            const posts = hydraData["hydra:member"] || [];
            const totalItems = hydraData["hydra:totalItems"] || 0;
            const itemsPerPage = 20;
            const currentPage = pageParam;
            const hasMore = currentPage * itemsPerPage < totalItems;

            return {
                posts,
                pagination: {
                    page: currentPage,
                    limit: itemsPerPage,
                    hasMore,
                },
                feedType,
            } as PostFeedResponse;
        },
        getNextPageParam: (lastPage) => {
            return lastPage.pagination?.hasMore ? lastPage.pagination.page + 1 : undefined;
        },
        staleTime: 1000 * 60 * 2, // 2 minutes for feed
    });
};

// Get single post
export const useGetPost = (id: number) => {
    return useQuery({
        queryKey: [SOCIAL_POST_KEY, id],
        queryFn: async () => {
            const response = await loggedInApiCall.get(`/api/social/posts/${id}`);
            return response.data as Post;
        },
        enabled: !!id,
    });
};

// Create post
export const useCreatePost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreatePostData) => {
            // Check if we have file attachments (File objects for direct upload)
            const hasFileAttachments =
                data.attachments && data.attachments.length > 0 && data.attachments[0] instanceof File;

            // Check if we have pre-uploaded images in metadata
            const hasPreUploadedImages = data.metadata?.images && data.metadata.images.length > 0;

            if (hasFileAttachments) {
                // Use FormData for direct file uploads (legacy behavior)
                const formData = new FormData();
                formData.append("content", data.content);
                formData.append("type", data.type);
                formData.append("visibility", data.visibility);

                data.topics?.forEach((topicId) => formData.append("topics[]", topicId.toString()));

                data.attachments!.forEach((file) => formData.append("attachments[]", file));

                if (data.metadata) {
                    formData.append("metadata", JSON.stringify(data.metadata));
                }

                const response = await loggedInApiCall.post("/api/social/posts", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                return response.data as Post;
            } else {
                // Use JSON for posts with pre-uploaded images or simple posts
                const jsonData = {
                    content: data.content,
                    type: data.type,
                    visibility: data.visibility,
                    topics: data.topics || [],
                    metadata: data.metadata || null,
                    // Don't send attachments directly - they will be created server-side from metadata
                };

                const response = await loggedInApiCall.post("/api/social/posts", jsonData);
                return response.data as Post;
            }
        },
        onSuccess: (newPost) => {
            // Invalidate and refetch feed queries
            queryClient.invalidateQueries([SOCIAL_POSTS_KEY]);
            queryClient.invalidateQueries([SOCIAL_FEED_KEY]);

            // Invalidate topics cache as new hashtags might have been created
            queryClient.invalidateQueries(["topics"]);
            queryClient.invalidateQueries(["trendingTopics"]);

            // Add the new post to the beginning of all feeds
            queryClient.setQueriesData<any>([SOCIAL_FEED_KEY], (oldData: any) => {
                if (!oldData) return oldData;

                return {
                    ...oldData,
                    pages: oldData.pages.map((page: any, index: number) => {
                        if (index === 0) {
                            return {
                                ...page,
                                posts: [newPost, ...page.posts],
                            };
                        }
                        return page;
                    }),
                };
            });
        },
    });
};

// Update post
export const useUpdatePost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: number; data: UpdatePostData }) => {
            const response = await loggedInApiCall.put(`/api/social/posts/${id}`, data);
            return response.data as Post;
        },
        onSuccess: (updatedPost) => {
            // Update single post cache
            queryClient.setQueryData([SOCIAL_POST_KEY, updatedPost.id], updatedPost);

            // Update post in feeds
            queryClient.setQueriesData<any>([SOCIAL_FEED_KEY], (oldData: any) => {
                if (!oldData) return oldData;

                return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                        ...page,
                        posts: page.posts.map((post: Post) => (post.id === updatedPost.id ? updatedPost : post)),
                    })),
                };
            });

            queryClient.invalidateQueries([SOCIAL_POSTS_KEY]);

            // Invalidate topics cache as hashtags might have changed
            queryClient.invalidateQueries(["topics"]);
            queryClient.invalidateQueries(["trendingTopics"]);
        },
    });
};

// Delete post
export const useDeletePost = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            await loggedInApiCall.delete(`/api/social/posts/${id}`);
            return id;
        },
        onSuccess: (deletedId) => {
            // Remove from single post cache
            queryClient.removeQueries([SOCIAL_POST_KEY, deletedId]);

            // Remove from feeds
            queryClient.setQueriesData<any>([SOCIAL_FEED_KEY], (oldData: any) => {
                if (!oldData) return oldData;

                return {
                    ...oldData,
                    pages: oldData.pages.map((page: any) => ({
                        ...page,
                        posts: page.posts.filter((post: Post) => post.id !== deletedId),
                    })),
                };
            });

            queryClient.invalidateQueries([SOCIAL_POSTS_KEY]);

            // Invalidate topics cache as hashtags usage might have changed
            queryClient.invalidateQueries(["topics"]);
            queryClient.invalidateQueries(["trendingTopics"]);
        },
    });
};
